// Pure logic for the Stores panel. Deterministic so the store merging, validation and — most
// importantly — the Postgres array serialisation can be unit tested without mounting the form.

import { hoursOpenArray, updatedHolidayClosures } from '@/constants/hoursConstants';
import { stripPlaceholderPhone } from '@/utilities/formatPhoneNumber';
import { CLIENT_STORE_HOURS_FIELDS } from '../../mutations';
import { HOLIDAY_FIELDS, REQUIRED_FIELDS, REQUIRED_FIELD_LABELS } from './constants';

/**
 * Saved locations followed by unsaved new ones, each with its pending edits applied.
 *
 * `locationOverrides` is a sparse array indexed by *position in this merged list*, so a new
 * store's edits live at `locations.length + idx`. Keep that indexing — the select, the edit form
 * and the save all address stores by index.
 *
 * `maxPallets` is the `{ [location_id]: value }` map read by the separate Max Pallets query. It is
 * layered under the pending edits so the form shows the saved value; it never reaches a save
 * payload (see `storesPayload`). `pendingMaxPallets` holds values whose write failed, also keyed by
 * `location_id` so they stay on the right store when a refetch reorders the list. It sits between
 * the saved value and the staged edits.
 */
export const mergeStores = ({ locations, newStores, locationOverrides, maxPallets, pendingMaxPallets }) => {
    const saved = locations || [];
    const added = newStores || [];
    const overrides = locationOverrides || [];
    const savedMax = maxPallets || {};
    const pending = pendingMaxPallets || {};
    const maxFrom = (map, id) => (id in map ? { warehouse_max_pallets: map[id] } : {});

    return [
        ...saved.map((store, idx) => ({
            ...store,
            ...maxFrom(savedMax, store.location_id),
            ...maxFrom(pending, store.location_id),
            ...overrides[idx],
        })),
        ...added.map((store, idx) => ({ ...store, ...overrides[saved.length + idx] })),
    ];
};

/**
 * Defaults a location inherits from its client: the client's default hours, its default pickup
 * comments, and any holiday closures it has set. Only truthy holidays are copied, matching the
 * old behaviour — an unset holiday falls through to `updatedHolidayClosures`.
 */
export const storeDefaultsFor = (client) => {
    const defaultOpen = client?.default_store_hours || hoursOpenArray;

    return {
        ...updatedHolidayClosures,
        hours_open: defaultOpen,
        days_closed: defaultOpen.map((day) => day.day_num),
        shipper_pu_comments: client?.default_pickup_comments,
        qbo_class: '',
        ...HOLIDAY_FIELDS.reduce((acc, attr) => (client?.[attr] ? { ...acc, [attr]: client[attr] } : acc), {}),
    };
};

/**
 * The record the form is editing: defaults, overlaid with either the selected store or the
 * client-level edits when no store is selected (the "Default Hours" option).
 */
export const chosenRecord = ({ storeDefaults, stores, chosenStore, clientOverrides }) => ({
    ...storeDefaults,
    ...(Number.isInteger(chosenStore) ? { ...(stores || [])[chosenStore] } : { ...clientOverrides }),
});

/**
 * Required fields that are empty, as a `{ field: true }` map.
 *
 * NOTE: the caller decides whether to validate at all. The old panel guarded this with a truthy
 * check on the selected index, so store 0 was never validated — see `shouldValidate`.
 */
export const missingFields = (chosen) =>
    REQUIRED_FIELDS.reduce((acc, field) => {
        const value = chosen?.[field];
        return !value || value.length === 0 ? { ...acc, [field]: true } : acc;
    }, {});

/**
 * Whether the visible form validates its required fields.
 *
 * True when a location is selected, and true for the first-location registration form — that
 * form collects exactly REQUIRED_FIELDS but runs with no selection, so it used to save
 * completely unvalidated.
 *
 * False for the "Default Hours" view of a client that already has locations: it edits
 * client-level hours and holidays, not a location, so none of REQUIRED_FIELDS applies.
 */
const shouldValidate = ({ chosenStore, isFirstLocation }) => !!isFirstLocation || Number.isInteger(chosenStore);

export const errorsFor = ({ chosenStore, chosen, isFirstLocation }) =>
    shouldValidate({ chosenStore, isFirstLocation }) ? missingFields(chosen) : {};

// The `default_end_location: false` written onto other stores when the default moves.
const isDefaultEndClearOnly = (override) => {
    const keys = Object.keys(override);
    return keys.length === 1 && keys[0] === 'default_end_location' && override.default_end_location === false;
};

/**
 * Which locations the bulk save is judged on: every location with a staged edit, plus every new one.
 *
 * `UPDATE_STORES` writes every location, but many stored ones predate the required fields, and
 * gating on them would stop those clients saving even their default hours. Opening a location,
 * or clearing its default end location, doesn't count as an edit.
 */
export const validationTargets = ({ storeCount, locationOverrides, savedCount }) => {
    const targets = new Set();

    (locationOverrides || []).forEach((override, idx) => {
        if (override && idx < storeCount && !isDefaultEndClearOnly(override)) targets.add(idx);
    });

    // Newly added locations live at the tail of the merged list and are always this session's.
    for (let idx = savedCount; idx < storeCount; idx += 1) targets.add(idx);

    return [...targets].sort((a, b) => a - b);
};

/**
 * Touched locations that are missing required fields, with enough detail to point the user at
 * them — a disabled Save should never be a mystery.
 */
export const blockingLocations = ({ stores, targets }) =>
    (targets || []).reduce((acc, idx) => {
        const store = (stores || [])[idx];
        const missing = store ? Object.keys(missingFields(store)) : [];
        if (!missing.length) return acc;
        return [...acc, { idx, name: storeLabel(store), missing: missing.map((f) => REQUIRED_FIELD_LABELS[f] || f) }];
    }, []);

/**
 * A JS array of day numbers as a Postgres array literal: `[0,1,2]` -> `'{0,1,2}'`.
 * Not a JSON encode: `days_closed` is `integer[]` and rejects one.
 */
const toPgIntArray = (values) => `{${(values || []).join(',')}}`;

/**
 * A location in the shape every location write sends: placeholder phone stripped, `days_closed` as a
 * Postgres literal, and Max Pallets (raw text while being typed) normalized when present.
 */
const toLocationRow = (location) => ({
    ...location,
    contact_phone: stripPlaceholderPhone(location.contact_phone),
    days_closed: toPgIntArray(location.days_closed),
    ...('warehouse_max_pallets' in location
        ? { warehouse_max_pallets: normalizeMaxPallets(location.warehouse_max_pallets) }
        : {}),
});

/** Location insert payload. */
export const locationPayload = ({ chosen }) => toLocationRow(chosen);

/** First-location payload: the location, plus the client-level holiday and hours columns. */
export const firstLocationPayload = ({ client, chosen, clientOverrides }) => ({
    locations_input_object: toLocationRow({ client_id: client.client_id, ...chosen }),
    client_id: client.client_id,
    hours: clientOverrides?.hours_open,
    ...Object.fromEntries(HOLIDAY_FIELDS.map((field) => [field, chosen[field]])),
});

/**
 * The client's store-hour columns for `update_clients_by_pk`, with default hours carried across.
 * Only those columns go out: the user role can update them but not insert a client row.
 */
export const clientPayload = ({ client, clientOverrides }) => {
    const merged = { ...client, ...clientOverrides, default_store_hours: clientOverrides?.hours_open };
    return CLIENT_STORE_HOURS_FIELDS.reduce(
        (acc, field) => (merged[field] === undefined ? acc : { ...acc, [field]: merged[field] }),
        {}
    );
};

/**
 * Every store shaped for the bulk upsert. `geo_point` is generated and must not be written.
 * `warehouse_max_pallets` is stripped from saved rows (those with a `location_id`): the bulk
 * upsert must not depend on that column, and an upsert row that omits it would reset it to null
 * if it were an update column. Saved rows are written by `maxPalletsChanges` instead. A new row
 * has no `location_id`, so it inserts and carries its own typed value (null included).
 */
export const storesPayload = ({ stores }) =>
    (stores || []).map(({ __typename, geo_point, ...rest }) => {
        if (rest.location_id) delete rest.warehouse_max_pallets;
        return toLocationRow(rest);
    });

/**
 * The Max Pallets writes a save owes: one `{ location_id, warehouse_max_pallets }` per saved
 * location whose value the user edited this session or whose earlier write failed (in
 * `pendingMaxPallets`). The value is normalized, and `null` means cleared. Untouched locations are
 * never listed, so a save can't overwrite a stored value it didn't change. New, unsaved stores are
 * excluded; their value travels in the insert.
 */
export const maxPalletsChanges = ({ stores, locationOverrides, savedCount, pendingMaxPallets }) =>
    (stores || []).slice(0, savedCount).reduce((acc, store, idx) => {
        const override = (locationOverrides || [])[idx];
        const edited = !!override && 'warehouse_max_pallets' in override;
        const pending = store.location_id in (pendingMaxPallets || {});
        if (!store.location_id || !(edited || pending)) return acc;
        return [
            ...acc,
            { location_id: store.location_id, warehouse_max_pallets: normalizeMaxPallets(store.warehouse_max_pallets) },
        ];
    }, []);

/**
 * Toggling a day open/closed. A closed day parks both ends at the sentinel `'closed'`; reopening
 * puts them at 12. `days_closed` is always recomputed from the whole week so the two stay in step.
 */
export const toggleDay = (hoursOpen, idx) => {
    const clone = [...(hoursOpen || [])];
    const day = clone[idx];
    if (!day) return { hours_open: clone, days_closed: closedDayNums(clone) };

    clone[idx] = {
        ...day,
        start: day.start === 'closed' ? 12 : 'closed',
        close: day.close === 'closed' ? 12 : 'closed',
    };
    return { hours_open: clone, days_closed: closedDayNums(clone) };
};

const closedDayNums = (hoursOpen) =>
    (hoursOpen || []).filter((day) => day.start === 'closed').map((day) => day.day_num);

/**
 * Setting one end of a day's hours. Changing the opening time recomputes `days_closed`; changing
 * the closing time does not, because only `start` marks a day closed.
 */
export const setDayHours = (hoursOpen, idx, edge, value) => {
    const clone = [...(hoursOpen || [])];
    if (!clone[idx]) return { hours_open: clone };

    clone[idx] = { ...clone[idx], [edge]: value };
    return edge === 'start' ? { hours_open: clone, days_closed: closedDayNums(clone) } : { hours_open: clone };
};

/**
 * Indices of every store other than `exceptIdx` that currently claims `default_end_location`.
 * The flag is exclusive, so selecting it on one store has to clear the others.
 */
export const storesToClearDefaultEnd = (stores, exceptIdx) =>
    (stores || []).reduce(
        (acc, store, idx) => (idx !== exceptIdx && store?.default_end_location === true ? [...acc, idx] : acc),
        []
    );

export const isDayOpen = (chosen, idx) => chosen?.hours_open && chosen.hours_open[idx]?.start !== 'closed';

/** Reads an hours cell defensively — the old panel indexed unguarded and could throw. */
export const dayHourValue = (chosen, idx, edge) => chosen?.hours_open?.[idx]?.[edge] ?? '';

export const storeLabel = (store) => store?.location_name || '(no location name)';

/** Largest value a Postgres `integer` column holds. */
export const MAX_PALLETS_LIMIT = 2147483647;

/**
 * Turns the Max Pallets text input into a value that is safe to save.
 * Cuts the text at the first '.' so digits are never joined across a decimal separator
 * ('12.5' -> 12, '1,500.00' -> 1500, '.5' -> null), then keeps only digits. Returns `null` when
 * nothing is left (empty or whitespace), and clamps anything above the Postgres integer maximum
 * (2147483647) down to it. A number is treated as its string form.
 * @param {string|number|null|undefined} input Raw text from the field.
 * @returns {number|null} A whole number from 0 to 2147483647, or null for "not set".
 */
export const normalizeMaxPallets = (input) => {
    const digits = String(input ?? '')
        .split('.')[0]
        .replace(/\D/g, '');
    if (!digits) return null;
    return Math.min(Number(digits), MAX_PALLETS_LIMIT);
};
